import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/format-currency';
import { bestOption, type SurfaceItem } from '@/lib/monetization';

export interface SurfaceReminderProps {
  item: SurfaceItem;
  /** Open the full detail sheet for this surface. */
  onOpen: () => void;
  /** Dismiss the reminder without opening (won't re-show this session). */
  onDismiss: () => void;
}

/** Reminder title + one-line hook for a surface, per type. */
function reminderCopy(item: SurfaceItem): { title: string; hook: string } {
  const p = item.presentation ?? {};
  if (item.type === 'GIFT') {
    const gift = item.gift ?? null;
    return {
      title: p.title ?? p.headline ?? 'Gift',
      hook: gift ? `${formatCurrency(gift.amountCents)} waiting!` : 'A reward is waiting!',
    };
  }
  if (item.type === 'OFFER') {
    const best = bestOption(item.offer?.options ?? []);
    return {
      title: p.title ?? p.headline ?? 'Offer',
      hook: best ? `+${best.bonusPercent}% deposit bonus waiting` : 'An offer is waiting',
    };
  }
  return { title: p.title ?? p.headline ?? 'Update', hook: p.subtitle ?? p.body ?? 'Tap to view' };
}

/**
 * Small reminder that slides up from the bottom of the lobby (REMINDER display
 * mode) to nudge the player that a surface is waiting. Rendered in a portal,
 * fixed above the {@link BottomNav}; tapping it opens the full detail sheet,
 * the ✕ dismisses it for the session. Mirrors the lavender promo styling of
 * {@link SurfaceCard}.
 */
export function SurfaceReminder({ item, onOpen, onDismiss }: SurfaceReminderProps) {
  const { title, hook } = reminderCopy(item);
  const imageUrl = typeof item.presentation?.imageUrl === 'string' ? item.presentation.imageUrl : undefined;

  return createPortal(
    <div
      className={cn(
        'fixed left-1/2 z-40 w-full max-w-[450px] -translate-x-1/2 px-3.5',
        // Clear the mobile bottom-nav (82px) + iOS safe area; on md+ the nav is
        // a left sidebar, so a small bottom margin is enough.
        'bottom-[calc(94px+env(safe-area-inset-bottom))] md:bottom-6',
        'animate-slide-up',
      )}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2.5 rounded-2xl border-2 border-stroke-soft bg-input-soft p-2 shadow-[0_12px_32px_-12px_rgba(114,28,183,0.55)]">
        <button
          type="button"
          onClick={onOpen}
          aria-label={`${title} — ${hook}`}
          className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
        >
          <div className="size-10 shrink-0 overflow-hidden rounded-lg">
            {imageUrl ? (
              <img src={imageUrl} alt="" className="size-full object-cover" />
            ) : (
              <div className="size-full bg-linear-to-br from-accent-soft-light to-accent-soft" />
            )}
          </div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="truncate text-[14px] font-semibold text-ink">{title}</p>
            <p className="truncate text-[12px] text-ink-muted">{hook}</p>
          </div>
          <span className="inline-flex h-8 shrink-0 items-center rounded-pill bg-[linear-gradient(135deg,var(--color-brand-grad-3)_0%,var(--color-brand-grad-2)_50%,var(--color-brand-grad-1)_100%)] px-3.5 text-[13px] font-semibold text-white">
            View
          </span>
        </button>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="grid size-7 shrink-0 place-items-center rounded-full text-ink-muted transition-colors hover:bg-black/5"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>,
    document.body,
  );
}
